/* Keep collection browsing on one URL while loading further Shopify result pages as needed. */
(() => {
  if (window.CLAECollectionAutoloadInstalled) return;
  window.CLAECollectionAutoloadInstalled = true;

  let current = null;
  const failedLists = new WeakSet();

  function clearCurrent() {
    if (!current) return;
    current.intersectionObserver.disconnect();
    current.controller?.abort();
    current.sentinel.remove();
    current = null;
  }

  function finish(state) {
    state.intersectionObserver.disconnect();
    state.sentinel.remove();
    state.nav.remove();
    if (current === state) current = null;
  }

  async function loadNext(state) {
    if (state.loading || !state.list.isConnected) return;
    const nextLink = state.nav.querySelector('a[rel="next"]');
    if (!nextLink) return finish(state);

    state.loading = true;
    state.sentinel.textContent = document.documentElement.lang.startsWith("fr") ? "Chargement des produits…" : "Loading products…";
    const controller = new AbortController();
    state.controller = controller;

    try {
      const url = new URL(nextLink.getAttribute("href"), location.href);
      url.searchParams.set("section_id", state.section.id.replace(/^shopify-section-/, ""));
      const response = await fetch(url, { credentials: "same-origin", signal: controller.signal });
      if (!response.ok) throw new Error(`Collection page returned ${response.status}`);

      const doc = new DOMParser().parseFromString(await response.text(), "text/html");
      const incomingSection = doc.querySelector(".shopify-section--main-collection");
      const incomingList = incomingSection?.querySelector(".collection__main > product-list");
      if (!incomingList || !incomingList.children.length) throw new Error("No products in next collection page");
      if (current !== state || !state.list.isConnected) return;

      const fragment = document.createDocumentFragment();
      for (const child of incomingList.children) {
        const item = document.importNode(child, true);
        item.removeAttribute("reveal-on-scroll");
        item.querySelectorAll('[reveal-on-scroll="true"]').forEach(el => el.removeAttribute("reveal-on-scroll"));
        fragment.append(item);
      }
      state.list.append(fragment);

      const incomingNav = incomingSection.querySelector(".collection__main > .pagination");
      if (!incomingNav?.querySelector('a[rel="next"]')) return finish(state);
      state.nav.replaceChildren(...Array.from(incomingNav.childNodes, node => document.importNode(node, true)));
      state.sentinel.textContent = "";
    } catch (error) {
      if (error.name === "AbortError") return;
      // If automatic loading fails, restore Shopify's working pagination links.
      failedLists.add(state.list);
      state.intersectionObserver.disconnect();
      state.nav.style.removeProperty("display");
      state.nav.removeAttribute("aria-hidden");
      state.sentinel.remove();
      if (current === state) current = null;
    } finally {
      state.loading = false;
      state.controller = null;
    }
  }

  function setup() {
    const section = document.querySelector(".shopify-section--main-collection");
    const list = section?.querySelector(".collection__main > product-list");
    if (current?.list === list) return;
    clearCurrent();
    if (!section || !list) return;
    if (failedLists.has(list)) return;

    // Keep legacy page URLs usable when someone follows a pre-existing deep link.
    if (Number(new URLSearchParams(location.search).get("page") || 1) > 1) return;
    const nav = section.querySelector(".collection__main > .pagination");
    if (!nav?.querySelector('a[rel="next"]')) return;

    const sentinel = document.createElement("div");
    sentinel.className = "clae-collection-autoload";
    sentinel.style.minHeight = "1px";
    sentinel.setAttribute("role", "status");
    sentinel.setAttribute("aria-live", "polite");
    nav.style.display = "none";
    nav.setAttribute("aria-hidden", "true");
    nav.after(sentinel);

    const state = { section, list, nav, sentinel, loading: false, controller: null, intersectionObserver: null };
    state.intersectionObserver = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) loadNext(state);
    }, { rootMargin: "800px 0px" });
    current = state;
    state.intersectionObserver.observe(sentinel);
  }

  const mutationObserver = new MutationObserver(setup);
  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", setup, { once: true });
  else setup();
})();
